import dayjs from "lib/dayjs";
import { addKpiCards } from "reporting/pdf/addKpiCards";
import { addHorizontalStackedBarChart } from "reporting/pdf/addHorizontalStackedBarChart";
import { addReportTable } from "reporting/pdf/addReportTable";
import { addStackedBarChart } from "reporting/pdf/addStackedBarChart";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { fmtCurrency, safeFilePart } from "reporting/pdf/pdfFormatters";

const PDF_COLORS = [
  [47, 109, 232], [151, 45, 218], [36, 164, 116], [222, 78, 78],
  [95, 176, 172], [84, 154, 76], [176, 122, 161], [242, 142, 43],
  [236, 193, 57], [159, 130, 112], [89, 139, 210], [225, 114, 98],
];
const OTHER_COLOR = [145, 145, 145];

function componentValue(componentRow, groupKey, visibleSeries) {
  if (!componentRow) return 0;
  if (groupKey === "__total") return Number(componentRow.value || 0);
  if (groupKey !== "__other_series") return Number(componentRow.seriesValues?.[groupKey] || 0);
  const visibleKeys = visibleSeries.filter((item) => item.key !== "__other_series").map((item) => item.key);
  return Number(componentRow.value || 0) - visibleKeys.reduce((sum, key) => sum + Number(componentRow.seriesValues?.[key] || 0), 0);
}

export function buildCapitalFlowOverviewPdfTable(context = {}) {
  const result = context.result || { rows: [], series: [] };
  const series = result.series || [];
  const groups = series.length ? [...series, { key: "__total", label: "Total" }] : [{ key: "__total", label: "Total" }];

  if (context.measure === "allocatedDebtService" && context.debtServiceComponents) {
    const principalRows = new Map(context.debtServiceComponents.principal.rows.map((row) => [row.id, row]));
    const interestRows = new Map(context.debtServiceComponents.interest.rows.map((row) => [row.id, row]));
    return {
      title: "Chart Data",
      columns: [
        { key: "category", label: "Category" },
        ...(series.length ? [{ key: "breakdown", label: "Breakdown" }] : []),
        { key: "principal", label: "Principal", align: "right", format: (value) => fmtCurrency(value, 2) },
        { key: "interest", label: "Interest", align: "right", format: (value) => fmtCurrency(value, 2) },
        { key: "debtService", label: "Debt Service", align: "right", format: (value) => fmtCurrency(value, 2) },
      ],
      rows: result.rows.flatMap((row) => groups.map((group) => {
        const principal = componentValue(principalRows.get(row.id), group.key, series);
        const interest = componentValue(interestRows.get(row.id), group.key, series);
        return { category: row.label, breakdown: group.label, principal, interest, debtService: principal + interest };
      })),
    };
  }

  return {
    title: "Chart Data",
    columns: [
      { key: "category", label: "Category" },
      ...(series.length ? [{ key: "breakdown", label: "Breakdown" }] : []),
      { key: "value", label: context.measureLabel || "Value", align: "right", format: (value) => fmtCurrency(value, 2) },
    ],
    rows: result.rows.flatMap((row) => groups.map((group) => ({
      category: row.label,
      breakdown: group.label,
      value: group.key === "__total" ? Number(row.value || 0) : Number(row.seriesValues?.[group.key] || 0),
    }))),
  };
}

function prepareChart(context) {
  const sourceRows = context.result.rows || [];
  const sourceSeries = context.result.series?.length
    ? context.result.series
    : [{ key: "value", label: context.measureLabel || "Value" }];
  const rankedSeries = sourceSeries.map((item) => ({
    ...item,
    total: sourceRows.reduce((sum, row) => sum + Number(item.key === "value" ? row.value : row.seriesValues?.[item.key] || 0), 0),
  })).sort((left, right) => right.total - left.total);
  const visibleSeries = rankedSeries.slice(0, 12);
  const hiddenSeries = rankedSeries.slice(12);
  const series = [
    ...visibleSeries.map((item, index) => ({ ...item, color: PDF_COLORS[index % PDF_COLORS.length] })),
    ...(hiddenSeries.length ? [{ key: "__pdf_other_series", label: `Other (${hiddenSeries.length})`, color: OTHER_COLOR }] : []),
  ];
  const rows = sourceRows.map((row) => ({
    label: row.label,
    date: row.label,
    ...Object.fromEntries(visibleSeries.map((item) => [item.key, Number(item.key === "value" ? row.value : row.seriesValues?.[item.key] || 0)])),
    ...(hiddenSeries.length ? { __pdf_other_series: hiddenSeries.reduce((sum, item) => sum + Number(row.seriesValues?.[item.key] || 0), 0) } : {}),
  }));
  if (context.view === "time" || rows.length <= 15) return { rows, series, categoryOverflow: 0, seriesOverflow: hiddenSeries.length };
  const visibleRows = rows.slice(0, 15);
  const hiddenRows = rows.slice(15);
  visibleRows.push({
    label: `Other (${hiddenRows.length})`,
    ...Object.fromEntries(series.map((item) => [item.key, hiddenRows.reduce((sum, row) => sum + Number(row[item.key] || 0), 0)])),
  });
  return { rows: visibleRows, series, categoryOverflow: hiddenRows.length, seriesOverflow: hiddenSeries.length };
}

function addConfiguredChart(ctx, context, startY) {
  const chart = prepareChart(context);
  const notes = [
    chart.categoryOverflow ? `Chart displays the first 15 categories in the selected order; ${chart.categoryOverflow} additional categories are combined as Other. Complete results appear in Chart Data.` : "",
    chart.seriesOverflow ? `The 12 largest breakdown series are shown; ${chart.seriesOverflow} additional series are combined as Other.` : "",
  ].filter(Boolean).join(" ");
  if (context.view === "time") {
    return addStackedBarChart(ctx, { title: context.chartTitle, rows: chart.rows, series: chart.series, height: 250 }, startY);
  }
  return addHorizontalStackedBarChart(ctx, {
    title: context.chartTitle,
    subtitle: context.timeframeTitle,
    note: notes,
    rows: chart.rows,
    series: chart.series,
  }, startY);
}

export default async function exportCapitalFlowOverviewPdf({ orgId, orgName, kpis = [], context, save = true } = {}) {
  if (!context?.result) throw new Error("Configure the Allocation Overview chart before exporting.");
  const ctx = await createCompassPdf({
    orgId,
    orgName,
    reportTitle: "CapitalFlow - Overview",
    asOfLabel: context.timeframeTitle || "",
    createdStamp: new Date(),
    orientation: "portrait",
    branding: "unbranded",
  });
  let y = ctx.margins.top + 10;
  y = addKpiCards(ctx, kpis, y, { columns: 3 });
  y = addConfiguredChart(ctx, context, y);
  addReportTable(ctx, { ...buildCapitalFlowOverviewPdfTable(context), fontSize: 7 }, y);
  ctx.finalize();
  const fileName = `CapitalFlow_Overview_${safeFilePart(orgName || "Organization")}_${dayjs().format("YYYY-MM-DD")}.pdf`;
  if (save) ctx.doc.save(fileName);
  return { doc: ctx.doc, fileName };
}
